import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import FlashQuizMain from './FlashQuizMain';
import FlashQuizNotAvailable from './FlashQuizNotAvailable';
import InstaQuizStatusScreen from './InstaQuizStatusScreen';
import InstaQuizJoinWelcome from './InstaQuizJoinWelcome';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../../../context/AuthContext';
import AuthModal from '../../auth/AuthModal';
import { getGuestDisplayName } from '../../../../utils/guestDisplayUtils';

const FlashQuizWelcome = ({ onBack, forceJoin = false, onPractice }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated } = useAuthContext();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Verify if participant is still valid
  const verifyParticipant = useCallback(async (sessionId, participantId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/participant/${participantId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.participant) {
          return data.participant;
        }
      }
      return null;
    } catch (error) {
      console.error('[FlashQuizWelcome] Error verifying participant:', error);
      return null;
    }
  }, []);

  // Join a session
  const joinSession = useCallback(async (session) => {
    try {
      setJoining(true);
      // console.log('[FlashQuizWelcome] Joining session:', session.session_id);
      // console.log('[FlashQuizWelcome] User data:', user);
      
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Join request timed out')), 10000)
      );

      // Build proper guest_name for guest auth; for registered users, rely on user_id
      const computedGuestName = (() => {
        if (!user) return null;
        if (user.user_type === 'guest') {
          return getGuestDisplayName(user);
        }
        const fname = (user.fname || '').trim();
        const lname = (user.lname || '').trim();
        const full = `${fname} ${lname}`.trim();
        return full || null;
      })();

      const joinPromise = fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/join`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
          },
          body: JSON.stringify({
            user_id: user?.user_id || null,
            guest_name: user?.user_type === 'guest' ? computedGuestName : null,
            device_id: localStorage.getItem('device_id') || `device_${Date.now()}`
          })
        }
      );

      const joinResponse = await Promise.race([joinPromise, timeoutPromise]);

      if (!joinResponse.ok) {
        const errorData = await joinResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to join session');
      }

      const joinData = await joinResponse.json();
      // console.log('[FlashQuizWelcome] Successfully joined session - Response:', joinData);
      
      if (joinData.success && joinData.participant_id) {
        // Create participant object from response
        const participantData = {
          participant_id: joinData.participant_id,
          user_id: user?.user_id || null,
          session_id: session.session_id,
          guest_name: user ? getGuestDisplayName(user) : null,
          user_type: joinData.user_type || 'guest'
        };
        
        setParticipant(participantData);
        // console.log('[FlashQuizWelcome] Participant state set:', participantData);
        enqueueSnackbar(`You're in! Welcome to ${session.quiz_name || 'Insta Quiz'}.`, { variant: 'success' });
      } else {
        console.error('[FlashQuizWelcome] No participant_id in response');
        throw new Error('No participant data received');
      }
      
    } catch (error) {
      console.error('[FlashQuizWelcome] Error joining session:', error);
      
      // If it's a timeout or network error, check if user might already be a participant
      if (error.message.includes('timed out') || error.message.includes('Failed to fetch')) {
        // Try to verify participant instead
        const storedParticipantId = localStorage.getItem('flash_participant_id');
        if (storedParticipantId) {
          // console.log('[FlashQuizWelcome] Join timed out, verifying stored participant...');
          const participant = await verifyParticipant(session.session_id, storedParticipantId);
          if (participant) {
            // console.log('[FlashQuizWelcome] Found valid participant, resuming...');
            
            // Construct guest_name from either guest_name field or fname+lname for registered users
            let guestName = participant.guest_name;
            if (!guestName && participant.fname && participant.lname) {
              guestName = `${participant.fname} ${participant.lname}`;
            }
            
            setParticipant({
              participant_id: participant.participant_id,
              user_id: participant.user_id,
              session_id: session.session_id,
              guest_name: guestName,
              user_type: participant.user_type || 'guest',
              current_score: participant.current_score || 0
            });
            setLoading(false);
            enqueueSnackbar('Welcome back — your spot is still saved!', { variant: 'success' });
            return;
          }
        }
        enqueueSnackbar('Almost there — give it another try in a moment.', { variant: 'warning' });
      } else {
        enqueueSnackbar(error.message || "Couldn't join this round — please try again.", { variant: 'error' });
      }
      
      setParticipant(null);
      setLoading(false);
    } finally {
      setJoining(false);
    }
  }, [user, enqueueSnackbar, verifyParticipant]);

  // Check for active session and join if authenticated
  const checkAndJoinSession = useCallback(async () => {
    try {
      setLoading(true);
      // console.log('[FlashQuizWelcome] Checking for active sessions...');
      
      // Fetch active sessions
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/flash/active`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }
      
      const data = await response.json();
      const activeSessions = data.sessions || [];
      // console.log('[FlashQuizWelcome] Active sessions found:', activeSessions.length);
      
      if (activeSessions.length === 0) {
        // No active session
        setActiveSession(null);
        setParticipant(null);
        // Clear any stored flash session data
        localStorage.removeItem('flash_session_id');
        localStorage.removeItem('flash_participant_id');
        setLoading(false);
        return;
      }
      
      const session = activeSessions[0];
      setActiveSession(session);
      
      // Check if we have a stored participant and verify it's still valid FIRST
      const storedSessionId = localStorage.getItem('flash_session_id');
      const storedParticipantId = localStorage.getItem('flash_participant_id');
      
      if (storedSessionId === String(session.session_id) && storedParticipantId) {
        // console.log('[FlashQuizWelcome] Found stored participant, verifying...');
        
        try {
          const participant = await verifyParticipant(session.session_id, storedParticipantId);
          
          if (participant) {
            // console.log('[FlashQuizWelcome] Stored participant is valid, resuming session');
            
            // Construct guest_name from either guest_name field or fname+lname for registered users
            let guestName = participant.guest_name;
            if (!guestName && participant.fname && participant.lname) {
              guestName = `${participant.fname} ${participant.lname}`;
            }
            
            setParticipant({
              participant_id: participant.participant_id,
              user_id: participant.user_id,
              session_id: session.session_id,
              guest_name: guestName,
              user_type: participant.user_type || 'guest',
              current_score: participant.current_score || 0
            });
            setLoading(false);
            return;
          } else {
            // console.log('[FlashQuizWelcome] Stored participant is invalid, clearing...');
            localStorage.removeItem('flash_session_id');
            localStorage.removeItem('flash_participant_id');
          }
        } catch (verifyError) {
          console.error('[FlashQuizWelcome] Error verifying participant:', verifyError);
          localStorage.removeItem('flash_session_id');
          localStorage.removeItem('flash_participant_id');
        }
      }
      
      // If forceJoin (from MYM force): auto-join or prompt auth
      if (forceJoin) {
        if (isAuthenticated && user) {
          await joinSession(session);
        } else {
          setShowAuthModal(true);
        }
        setLoading(false);
        return;
      }
      
      // If no valid stored participant, don't auto-join - show session info card instead
      // console.log('[FlashQuizWelcome] No stored participant, showing session info card');
      setLoading(false);
      return;
      
    } catch (error) {
      console.error('[FlashQuizWelcome] Error checking sessions:', error);
      enqueueSnackbar("Couldn't find a live session right now — hang tight!", { variant: 'error' });
      setActiveSession(null);
      setParticipant(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, joinSession, verifyParticipant, enqueueSnackbar, forceJoin, user]);

  // Handle successful authentication
  const handleAuthSuccess = useCallback(async () => {
    setShowAuthModal(false);
    if (activeSession) {
      await joinSession(activeSession);
    }
  }, [activeSession, joinSession]);

  // Initial check on mount only
  useEffect(() => {
    checkAndJoinSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Global websocket to auto-react when an admin activates/ends a session
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const socket = io(baseUrl, { transports: ['websocket'] });
    socket.on('connect', () => {
      socket.emit('flash:join-global');
    });
    
    // Only react to activation - move from "no session" to "join session" state
    socket.on('flash:activated', async () => {
      // Only check if user is NOT already participating
      // If they're already in a session, FlashQuizMain will handle it
      if (!participant) {
        await checkAndJoinSession();
      }
    });
    
    // Don't react to session_ended here - let FlashQuizMain handle showing results
    // If we call checkAndJoinSession() here, it will clear activeSession and 
    // replace FlashQuizMain with FlashQuizNotAvailable
    // socket.on('flash:session_ended') - intentionally removed
    
    return () => {
      try { socket.emit('flash:leave-global'); socket.disconnect(); } catch (_) {}
    };
  }, [checkAndJoinSession, participant]);

  // Handle refresh
  const handleRefresh = () => {
    checkAndJoinSession();
  };

  const handleBackToJoinWelcome = useCallback(() => {
    setParticipant(null);
    setJoining(false);
    localStorage.removeItem('flash_participant_id');
  }, []);

  // Loading — checking for sessions
  if (loading && !joining) {
    return <InstaQuizStatusScreen variant="checking" />;
  }

  // Joining session
  if (joining) {
    return <InstaQuizStatusScreen variant="joining" />;
  }

  // No active session
  if (!activeSession) {
    return (
      <FlashQuizNotAvailable 
        onBack={onBack} 
        onRefresh={handleRefresh}
        onPractice={onPractice}
      />
    );
  }

  // Debug logging
  // console.log('[FlashQuizWelcome] Render state:', {
  //   loading,
  //   activeSession: !!activeSession,
  //   isAuthenticated,
  //   participant: !!participant
  // });

  // Active session with participant - show main quiz (don't require authentication)
  if (activeSession && participant) {
    // console.log('[FlashQuizWelcome] Rendering FlashQuizMain');
    return (
      <FlashQuizMain
        session={activeSession}
        participant={participant}
        onBack={handleBackToJoinWelcome}
        onComplete={() => {
          setActiveSession(null);
          setParticipant(null);
          // Clear stored flash session data
          localStorage.removeItem('flash_session_id');
          localStorage.removeItem('flash_participant_id');
          onBack();
        }}
      />
    );
  }

  // Active session but not joined yet — welcome screen
  if (activeSession && !participant) {
    const rawRapid = activeSession?.rapid_config;
    const parsedRapid = typeof rawRapid === 'string' ? (() => {
      try { return JSON.parse(rawRapid); }
      catch { return {}; }
    })() : (rawRapid || {});

    const handleJoinClick = () => {
      if (!isAuthenticated) {
        setShowAuthModal(true);
      } else {
        joinSession(activeSession);
      }
    };

    return (
      <>
        <InstaQuizJoinWelcome
          sessionName={activeSession.quiz_name}
          parsedRapid={parsedRapid}
          isAuthenticated={isAuthenticated}
          joining={joining}
          onJoin={handleJoinClick}
        />
        <AuthModal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  // Fallback
  return (
    <FlashQuizNotAvailable 
      onBack={onBack} 
      onRefresh={handleRefresh}
      onPractice={onPractice}
    />
  );
};

export default FlashQuizWelcome;
